import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { DocumentReference, TemplateCodeParameter } from "../ars_ldm";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARSText } from "../inputs/ARSText";
import { isAnalysisProgrammingCodeTemplate } from "../typeGuards/isAnalysisProgrammingCodeTemplate";
import { DocumentReferenceControl } from "./DocumentReferenceControl";
import { TemplateCodeParameterControl } from "./TemplateCodeParameterControl";

/**
 * Props for the AnalysisProgrammingCodeTemplateControl component.
 */
export interface AnalysisProgrammingCodeTemplateControlProps {
  /** RFC 6901 JSON Pointer path to the AnalysisProgrammingCodeTemplate in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
}

/**
 * A component for managing AnalysisProgrammingCodeTemplate entities.
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the AnalysisProgrammingCodeTemplate in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @returns A React functional component rendering the programming code template editor.
 */
export const AnalysisProgrammingCodeTemplateControl = ({
  dataPath,
  disabled = false,
}: AnalysisProgrammingCodeTemplateControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();
  const codeTemplate = getFromDataPath(dataPath, state, isAnalysisProgrammingCodeTemplate);
  const parameters = codeTemplate?.parameters ?? [];

  /**
   * Handles adding a document reference for the code template.
   */
  const handleAddDocumentReference = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && codeTemplate && codeTemplate.documentRef === undefined) {
      const newDocumentReference: DocumentReference = {
        referenceDocumentId: "",
        pageRefs: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/documentRef`,
        value: newDocumentReference,
      });
    }
  };

  /**
   * Handles adding a template code parameter.
   */
  const handleAddParameter = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && codeTemplate) {
      const parameterCount = parameters.length;
      const newParameter: TemplateCodeParameter = {
        name: `Parameter_${parameterCount + 1}`,
        label: `Parameter ${parameterCount + 1}`,
        description: "",
        valueSource: "",
        value: [],
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/parameters/${parameterCount}`,
        value: newParameter,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3">
      <ARSText
        dataPath={`${dataPath}/context`}
        id={`${idRoute}-context`}
        label="Context"
        labelWidth={2}
        placeholder="Enter context"
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/code`}
        id={`${idRoute}-code`}
        label="Code"
        labelWidth={2}
        placeholder="Enter code"
        type="textarea"
        debounceMilliseconds={2000}
        rows={6}
        disabled={disabled}
      />

      {codeTemplate?.documentRef && (
        <DocumentReferenceControl
          dataPath={`${dataPath}/documentRef`}
          disabled={disabled}
        />
      )}

      <AsyncButton
        id={`${idRoute}-add-document-reference`}
        onClick={handleAddDocumentReference}
        className="d-flex align-items-center btn-sm btn-success mb-3"
        disabled={disabled || codeTemplate?.documentRef !== undefined}
      >
        <span className="me-2">Add document reference</span>
        <PlusCircle />
      </AsyncButton>

      {parameters.map((parameter, index) => (
        <TemplateCodeParameterControl
          key={`${parameter.name}-${index}`}
          dataPath={`${dataPath}/parameters/${index}`}
          disabled={disabled}
          siblingCount={parameters.length}
        />
      ))}

      <AsyncButton
        id={`${idRoute}-add-parameter`}
        onClick={handleAddParameter}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled}
      >
        <span className="me-2">Add parameter</span>
        <PlusCircle />
      </AsyncButton>
    </div>
  );
};

AnalysisProgrammingCodeTemplateControl.displayName = "AnalysisProgrammingCodeTemplateControl";
